import { Factory, OctagonAlert as ProblemIcon } from 'lucide-react'
import { Navigate, useSearchParams } from 'react-router-dom'
import {
  useListProblemOccurrencesQuery,
  useWorkScope,
  workScopeKey
} from '@axilon-apps/domains-ontology-ui-state'
import { Empty } from '../../components/primitives/Empty'
import { WorkError } from '../../components/problem-work/OccurrenceTasks'
import { DraftProblems } from '../../components/problem-work/DraftProblems'
import { ProblemSummaryRow } from '../../components/problem-work/ProblemSummaryRow'
import { groupOccurrences } from '../../components/problem-work/work-model'
import {
  OCCURRENCE_QUERY_PARAM,
  PROBLEMS_SCOPE_QUERY_PARAM,
  occurrenceHref
} from '../../lib/component-links'
import { cn } from '../../lib/utils'

export default function ProblemsPage() {
  const scope = useWorkScope()
  const [params] = useSearchParams()
  // Older links select a problem on the list; it now has its own page.
  const legacy = params.get(OCCURRENCE_QUERY_PARAM)
  if (legacy) return <Navigate to={occurrenceHref(legacy)} replace />
  return <ScopedProblemsPage key={workScopeKey(scope)} />
}

/** A short row per problem, grouped by location; each opens the problem's own page. */
function ScopedProblemsPage() {
  const scope = useWorkScope()
  const [params, setParams] = useSearchParams()
  const query = useListProblemOccurrencesQuery(scope, { refetchOnMountOrArgChange: true })
  const problems = query.isError ? [] : (query.currentData ?? [])
  const status = params.get(PROBLEMS_SCOPE_QUERY_PARAM) === 'closed' ? 'closed' : 'open'
  const groups = groupOccurrences(
    problems.filter((problem) => problem.status === status),
    (problem) => problem
  )
  const selectTab = (value: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set(PROBLEMS_SCOPE_QUERY_PARAM, value)
        return next
      },
      { replace: true }
    )
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-3">
      <header className="flex flex-col gap-3">
        <h1 className="m-0 text-2xl font-semibold tracking-tight text-ink">Problems</h1>
        <div
          role="group"
          aria-label="Problem status"
          className="flex items-center gap-6 border-b border-line"
        >
          {(['open', 'closed'] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={status === value}
              onClick={() => selectTab(value)}
              className={cn(
                'relative -mb-px inline-flex items-center gap-2 border-b-2 px-0.5 pb-2.5 pt-1 text-13 font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-brand motion-reduce:transition-none',
                status === value
                  ? 'border-brand text-ink'
                  : 'border-transparent text-ink-3 hover:text-ink'
              )}
            >
              {value === 'open' ? 'Open' : 'Closed'}
              {value === 'open' && query.currentData && !query.isError && (
                <span className="rounded bg-bg-sunk px-1.5 py-0.5 text-11 leading-none tabular-nums text-ink-3">
                  {problems.filter((problem) => problem.status === 'open').length}
                </span>
              )}
            </button>
          ))}
        </div>
      </header>
      {query.isError ? (
        <WorkError message="Couldn't load problems." retry={() => void query.refetch()} />
      ) : !query.currentData ? (
        <p role="status" className="m-0 py-8 text-13 text-ink-3">
          Loading problems…
        </p>
      ) : (
        <>
          {status === 'open' && <DraftProblems scope={scope} />}
          {!groups.length ? (
            <Empty title={`No ${status} problems`} icon={ProblemIcon} />
          ) : (
            <div className="flex min-w-0 flex-col gap-6 pt-1">
              {groups.map((group) => (
                <section
                  key={group.location ?? '__unset__'}
                  aria-label={group.location ?? 'Location not set'}
                  className="flex min-w-0 flex-col gap-2"
                >
                  <header className="flex items-center gap-2 px-1">
                    <Factory size={14} className="text-ink-3" />
                    <h2 className="m-0 text-13 font-medium text-ink-2">
                      {group.location ?? 'Location not set'}
                    </h2>
                    <span className="text-12 tabular-nums text-ink-3">{group.items.length}</span>
                  </header>
                  <ul className="m-0 flex list-none flex-col divide-y divide-line/70 overflow-hidden rounded-lg border border-line/80 bg-card p-0">
                    {group.items.map((problem) => (
                      <ProblemSummaryRow
                        key={problem.id}
                        occurrence={problem}
                        href={occurrenceHref(problem.id)}
                      />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
